"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Package, ChevronRight, FolderOpen, Printer, Trash2, AlertTriangle } from "lucide-react"
import { supabase } from "@/lib/supabaseClient"
import Link from "next/link"

type ItemStatus = "new" | "printed" | "qc_pass" | "qc_fail" | "binned" | "shipped" | "received"

type StatusCounts = {
  new: number
  printed: number
  qc_pass: number
  qc_fail: number
  binned: number
  shipped: number
  received: number
  total: number
}

export function PrintBatches() {
  const [isLoading, setIsLoading] = useState(true)
  const [statusCounts, setStatusCounts] = useState<StatusCounts>({
    new: 0,
    printed: 0,
    qc_pass: 0,
    qc_fail: 0,
    binned: 0,
    shipped: 0,
    received: 0,
    total: 0,
  })

  const fetchStatusCounts = async () => {
    try {
      // Fetch all batches
      const { data: batchData, error } = await supabase
        .from('batches')
        .select('id')

      if (error) {
        console.error('Error fetching batches:', error)
        return
      }

      // Fetch all items from all batches
      const batchIds = (batchData || []).map(batch => batch.id)
      
      if (batchIds.length === 0) {
        setStatusCounts({
          new: 0,
          printed: 0,
          qc_pass: 0,
          qc_fail: 0,
          binned: 0,
          shipped: 0,
          received: 0,
          total: 0,
        })
        return
      }

      const { data: items } = await supabase
        .from('order_items')
        .select('status')
        .in('batch_id', batchIds)

      // Calculate status counts across all items
      const counts = {
        new: 0,
        printed: 0,
        qc_pass: 0,
        qc_fail: 0,
        binned: 0,
        shipped: 0,
        received: 0,
        total: 0,
      }

      ;(items || []).forEach((item) => {
        const status = item.status as ItemStatus
        if (counts.hasOwnProperty(status)) {
          counts[status]++
        }
        counts.total++
      })

      setStatusCounts(counts)
    } catch (error) {
      console.error('Error in fetchStatusCounts:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    fetchStatusCounts()
  }, [])

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-5 bg-gray-200 rounded w-3/4 mb-4"></div>
          <div className="grid grid-cols-5 gap-4 mb-6">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="text-center">
                <div className="w-16 h-16 bg-gray-200 rounded-full mx-auto mb-2"></div>
                <div className="h-4 bg-gray-200 rounded mb-1"></div>
                <div className="h-4 bg-gray-200 rounded w-8 mx-auto"></div>
              </div>
            ))}
          </div>
          <div className="h-16 bg-gray-200 rounded mb-4"></div>
        </div>
        <div className="h-8 bg-gray-200 rounded animate-pulse"></div>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Total Items positioned at top right */}
      <div className="flex justify-end mb-6">
        <div className="text-right">
          <div className="text-2xl font-bold">{statusCounts.total}</div>
          <div className="text-xs text-muted-foreground">Total Items</div>
        </div>
      </div>

      {/* Horizontal Flow */}
      <div className="flex items-center justify-between mb-6">
        {/* Open */}
        <div className="flex items-center">
          <div className="flex flex-col items-center">
            <div className={`w-16 h-16 rounded-full border-4 flex items-center justify-center transition-all ${
              statusCounts.new > 0 ? "border-slate-500 bg-slate-50" : "border-gray-200 bg-gray-50"
            }`}>
              <FolderOpen className={`w-6 h-6 ${statusCounts.new > 0 ? "text-slate-600" : "text-gray-400"}`} />
            </div>
            <div className="mt-2 text-center">
              <div className="font-medium text-sm">Open</div>
              <Badge variant={statusCounts.new > 0 ? "default" : "secondary"} className={`mt-1 ${
                statusCounts.new > 0 ? "bg-slate-100 text-slate-700 hover:bg-slate-200" : ""
              }`}>
                {statusCounts.new}
              </Badge>
            </div>
          </div>
          <div className="flex-1 mx-4">
            <div className="h-1 bg-gray-200 rounded-full relative">
              <div className={`h-full rounded-full transition-all duration-500 ${
                statusCounts.new > 0 ? "bg-slate-400" : "bg-gray-300"
              }`} style={{ width: statusCounts.new > 0 ? "100%" : "20%" }} />
            </div>
          </div>
        </div>

        {/* Received */}
        <div className="flex items-center">
          <div className="flex flex-col items-center">
            <div className={`w-16 h-16 rounded-full border-4 flex items-center justify-center transition-all ${
              statusCounts.received > 0 ? "border-blue-500 bg-blue-50" : "border-gray-200 bg-gray-50"
            }`}>
              <Package className={`w-6 h-6 ${statusCounts.received > 0 ? "text-blue-600" : "text-gray-400"}`} />
            </div>
            <div className="mt-2 text-center">
              <div className="font-medium text-sm">Received</div>
              <Badge variant={statusCounts.received > 0 ? "default" : "secondary"} className={`mt-1 ${
                statusCounts.received > 0 ? "bg-blue-100 text-blue-700 hover:bg-blue-200" : ""
              }`}>
                {statusCounts.received}
              </Badge>
            </div>
          </div>
          <div className="flex-1 mx-4">
            <div className="h-1 bg-gray-200 rounded-full relative">
              <div className={`h-full rounded-full transition-all duration-500 ${
                statusCounts.received > 0 ? "bg-blue-400" : "bg-gray-300"
              }`} style={{ width: statusCounts.received > 0 ? "100%" : "20%" }} />
            </div>
          </div>
        </div>

        {/* Printed */}
        <div className="flex items-center">
          <div className="flex flex-col items-center">
            <div className={`w-16 h-16 rounded-full border-4 flex items-center justify-center transition-all ${
              statusCounts.printed > 0 ? "border-purple-500 bg-purple-50" : "border-gray-200 bg-gray-50"
            }`}>
              <Printer className={`w-6 h-6 ${statusCounts.printed > 0 ? "text-purple-600" : "text-gray-400"}`} />
            </div>
            <div className="mt-2 text-center">
              <div className="font-medium text-sm">Printed</div>
              <Badge variant={statusCounts.printed > 0 ? "default" : "secondary"} className={`mt-1 ${
                statusCounts.printed > 0 ? "bg-purple-100 text-purple-700 hover:bg-purple-200" : ""
              }`}>
                {statusCounts.printed}
              </Badge>
            </div>
          </div>
          <div className="flex-1 mx-4">
            <div className="h-1 bg-gray-200 rounded-full relative">
              <div className={`h-full rounded-full transition-all duration-500 ${
                statusCounts.printed > 0 ? "bg-purple-400" : "bg-gray-300"
              }`} style={{ width: statusCounts.printed > 0 ? "100%" : "20%" }} />
            </div>
          </div>
        </div>

        {/* Binned */}
        <div className="flex items-center">
          <div className="flex flex-col items-center">
            <div className={`w-16 h-16 rounded-full border-4 flex items-center justify-center transition-all ${
              statusCounts.binned > 0 ? "border-green-500 bg-green-50" : "border-gray-200 bg-gray-50"
            }`}>
              <Trash2 className={`w-6 h-6 ${statusCounts.binned > 0 ? "text-green-600" : "text-gray-400"}`} />
            </div>
            <div className="mt-2 text-center">
              <div className="font-medium text-sm">Binned</div>
              <Badge variant={statusCounts.binned > 0 ? "default" : "secondary"} className={`mt-1 ${
                statusCounts.binned > 0 ? "bg-green-100 text-green-700 hover:bg-green-200" : ""
              }`}>
                {statusCounts.binned}
              </Badge>
            </div>
          </div>
          <div className="flex-1 mx-4">
            <div className="h-1 bg-gray-200 rounded-full relative">
              <div className={`h-full rounded-full transition-all duration-500 ${
                statusCounts.binned > 0 ? "bg-green-400" : "bg-gray-300"
              }`} style={{ width: statusCounts.binned > 0 ? "100%" : "20%" }} />
            </div>
          </div>
        </div>

        {/* QC Fail */}
        <div className="flex flex-col items-center">
          <div className={`w-16 h-16 rounded-full border-4 flex items-center justify-center transition-all ${
            statusCounts.qc_fail > 0 ? "border-red-500 bg-red-50" : "border-gray-200 bg-gray-50"
          }`}>
            <AlertTriangle className={`w-6 h-6 ${statusCounts.qc_fail > 0 ? "text-red-600" : "text-gray-400"}`} />
          </div>
          <div className="mt-2 text-center">
            <div className="font-medium text-sm">QC Fail</div>
            <Badge variant={statusCounts.qc_fail > 0 ? "default" : "secondary"} className={`mt-1 ${
              statusCounts.qc_fail > 0 ? "bg-red-100 text-red-700 hover:bg-red-200" : ""
            }`}>
              {statusCounts.qc_fail}
            </Badge>
          </div>
        </div>
      </div>

      {/* View All Link */}
      <div className="pt-4 border-t mt-auto">
        <Link href="/batches">
          <Button variant="ghost" className="w-full justify-between text-sm font-medium hover:bg-gray-50">
            View All Print Batches
            <ChevronRight className="w-4 h-4" />
          </Button>
        </Link>
      </div>
    </div>
  )
}
